import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  Output,
  EventEmitter,
  inject,
  OnDestroy,
  input,
  InputSignal,
  EffectRef,
  effect,
  computed,
  Signal,
  ChangeDetectionStrategy,
  linkedSignal,
  WritableSignal,
  untracked,
  signal
} from '@angular/core';
import {DatePipe, NgOptimizedImage} from '@angular/common';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {fromEvent, Observable, of, forkJoin, combineLatest} from 'rxjs';
import {map, catchError, switchMap, debounceTime, tap} from 'rxjs/operators';
import {NewsTileContentDTO} from '../../../shared/types/home-types/newsTileContentDto.interface';
import {PillComponent} from '../../../shared/elements/pill/pill.component';
import {ContentComponent} from '../../../shared/elements/content/content.component';
import {CustomerDashboardTileResource} from '../../../shared/types/generated/customerDashboardTileResource';
import {NavService} from '../../../shared/services/nav.service';
import {ApiService} from '../../../shared/services/api.service';
import {SettingsMenuItemType} from '../../../shared/types/generated/settingsMenuItemType';
import {DashboardTileResource} from '../../../shared/types/generated/dashboardTileResource';
import {ModuleConfigurationService} from '../../../shared/services/module-configuration.service';
import {RouterService} from '../../../shared/services/router.service';
import {CapabilitiesService} from '../../../shared/services/capabilities.service';

@Component({
    selector: 'app-home-news-tiles',
    imports: [
        NgOptimizedImage,
        PillComponent,
        ContentComponent,
        DatePipe
    ],
    templateUrl: './news-tiles.component.html',
    styleUrls: ['./news-tiles.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsTilesComponent implements AfterViewInit, OnDestroy {
  public tileConfig: InputSignal<CustomerDashboardTileResource[]> = input.required<CustomerDashboardTileResource[]>();
  @Output() public hasLoadableTilesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('tilesContainer', {read: ElementRef})
  private tilesContainer!: ElementRef<HTMLElement>;
  @ViewChildren('tileEl', {read: ElementRef})
  private tileEls!: QueryList<ElementRef<HTMLElement>>;

  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private navService: NavService = inject(NavService);
  private api: ApiService = inject(ApiService);
  private modConfigService: ModuleConfigurationService = inject(ModuleConfigurationService);
  private router: RouterService = inject(RouterService);
  private capabilitiesService: CapabilitiesService = inject(CapabilitiesService);

  private resizeObs?: ResizeObserver;
  private rafId: number | null = null;
  private lastMeasuredWidth:number = -1;
  private rotationIntervalIds: Map<number, number> = new Map<number, number>();
  
  // Track which tile indexes have failed to load images
  public imageLoadErrors: WritableSignal<Set<number>> = signal(new Set<number>());
  
  private get isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  private tileConfigEffect: EffectRef = effect(() => {
    const enabledIndexes = this.enabledTileIndexes();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const contentMap = this.tileContent(); // Watch for content changes
    untracked(() => {
      this.stopNewsRotation();
      this.visibleTileIndex.set(enabledIndexes[0] ?? -1);
      this.startNewsRotation();
    });
  });

  public tileContent: WritableSignal<Map<number, NewsTileContentDTO[]>> = signal(new Map());

  public constructor() {
    // Use toObservable with switchMap to handle rapid signal changes
    combineLatest([
      toObservable(this.tileConfig),
      toObservable(this.navService.userMenu),
      toObservable(this.capabilitiesService.capabilities)
    ]).pipe(
      debounceTime(0), // Debounce to batch synchronous changes
      switchMap(([config, userMenu]) => {
        const requests: Observable<{index: number, contents: NewsTileContentDTO[]}>[] = [];

        config.forEach((conf, index) => {
          if (conf.enabled && this.hasAccessToTileType(conf.type)) {
            const correspondingMenu = userMenu.find(menu => menu.type === conf.type);
            // Do not create news tile if the user does not have access to the corresponding module
            if(correspondingMenu !== undefined) {
              const request = this.loadTileResources(conf, index, correspondingMenu.type).pipe(
                map((resources) => {
                  const contentList: NewsTileContentDTO[] = [];
                  resources.forEach((resource: DashboardTileResource) => {
                    const content: NewsTileContentDTO = {
                      id: String(resource.model.id),
                      parent_id: resource.model.parent_id ? String(resource.model.parent_id) : null,
                      category: correspondingMenu.name,
                      type: resource.model.type,
                      timestamp: resource.updated_at ?? '',
                      title: resource.title,
                      image: ((resource.image?.mimetype.includes('image')) ? resource.image?.url?.responsive : resource.image?.url?.preview) ?? '',
                      imageType: resource.image?.type,
                    }
                    contentList.push(content);
                  });
                  return {index, contents: contentList};
                }),
                catchError((error) => {
                  console.error(`Failed to load resources for tile index ${index} and type ${correspondingMenu.type}:`, error);
                  return of({index, contents: [] as NewsTileContentDTO[]});
                })
              );
              requests.push(request);
            }
          }
        });

        if (requests.length === 0) {
          return of([]);
        }

        return forkJoin(requests);
      }),
      tap(() => {
        // Mark initial load as complete when data arrives (even if empty)
        this.initialLoadComplete = true;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((results) => {
      const mappedContent = new Map<number, NewsTileContentDTO[]>();
      results.forEach(result => {
        if (result.contents.length > 0) {
          mappedContent.set(result.index, result.contents);
        }
      });
      this.tileContent.set(mappedContent);
    });
  }

  public visibleTileIndex: WritableSignal<number> = linkedSignal(() => 0);

  public visibleContentIndexes: WritableSignal<Map<number, number>> = linkedSignal(() => {
    const indexes = new Map<number, number>();
    this.tileConfig().forEach((tile, index) => {
      if (tile.enabled && this.hasAccessToTileType(tile.type)) {
        indexes.set(index, 0);
      }
    });
    return indexes;
  });

  public visibleTileContent: Signal<Map<number, NewsTileContentDTO | undefined>> = computed(() => {
    const visibleMap = new Map<number, NewsTileContentDTO | undefined>();
    const contentMap = this.tileContent();
    const indexMap = this.visibleContentIndexes();

    contentMap.forEach((items, tileIndex) => {
      const contentIndex = indexMap.get(tileIndex) ?? 0;
      visibleMap.set(tileIndex, items[contentIndex]);
    });

    return visibleMap;
  });

  public hasLoadableTiles: Signal<boolean> = computed(() => this.visibleTileContent().size > 0);
  private initialLoadComplete: boolean = false;
  private hasLoadableTilesEffect: EffectRef = effect(() => {
    const hasData = this.hasLoadableTiles();
    // Only emit once we've completed the initial async load
    // This prevents emitting false during the initial synchronous setup
    if (this.initialLoadComplete) {
      this.hasLoadableTilesChange.emit(hasData);
    }
  });
  
  // Effect to reset image errors when content changes
  private resetImageErrorsEffect: EffectRef = effect(() => {
    // Watch for any change in visible tile content
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const content = this.visibleTileContent();
    // Reset all image errors when content changes
    untracked(() => {
      this.imageLoadErrors.set(new Set<number>());
    });
  });


  private enabledTileIndexes: Signal<number[]> = linkedSignal(() => {
    const indexes: number[] = [];
    this.tileConfig().forEach((tile, index) => {
      if (tile.enabled && this.hasAccessToTileType(tile.type)) {
        indexes.push(index);
      }
    });
    return indexes;
  });


  public ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.tileEls.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.scheduleOverflowCheck(true));

    if ('ResizeObserver' in window) {
      this.resizeObs = new ResizeObserver(() => this.scheduleOverflowCheck());
      this.resizeObs.observe(this.tilesContainer.nativeElement);
    } else {
      fromEvent(window, 'resize')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.scheduleOverflowCheck());
    }

    requestAnimationFrame(() => this.scheduleOverflowCheck(true));
    setTimeout(() => this.scheduleOverflowCheck(true), 0);
  }

  public ngOnDestroy(): void {
    this.stopNewsRotation();

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.resizeObs && this.tilesContainer) {
      this.resizeObs.unobserve(this.tilesContainer.nativeElement);
      this.resizeObs = undefined;
    }
  }

  private scheduleOverflowCheck(force: boolean = false): void {
    const container = this.tilesContainer?.nativeElement;
    if (!container) return;

    const width = container.clientWidth;

    if (!force && width === this.lastMeasuredWidth && this.rafId === null) {
      return;
    }
    this.lastMeasuredWidth = width;

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.performOverflowCheck();
    });
  }

  private performOverflowCheck(): void {
    const container: HTMLElement | undefined = this.tilesContainer?.nativeElement;
    const tiles: HTMLElement[] = this.tileEls?.toArray().map(t => t.nativeElement) ?? [];
    if (!container || tiles.length === 0) return;
    
    // hidden (e.g. mobile view)
    if (container.offsetParent === null) {
      return;
    }

    // show all
    for (const el of tiles) {
      this.renderer.removeClass(el, 'd-none');
    }

    // force layout
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    container.clientHeight;

    const firstTop =  tiles[0].offsetTop;

    let lastFirstRowIndex = 0;
    for (let i = 0; i < tiles.length; i++) {
      const top = tiles[i].offsetTop
      if (top === firstTop) {
        lastFirstRowIndex = i;
      } else {
        break;
      }
    }

    for (let i = 0; i < tiles.length; i++) {
      const shouldHide = i > lastFirstRowIndex;
      if (shouldHide) {
        this.renderer.addClass(tiles[i], 'd-none');
      } else {
        this.renderer.removeClass(tiles[i], 'd-none');
      }
    }
  }

  private startNewsRotation(): void {
    if (!this.isBrowser) return;

    this.stopNewsRotation();

    const enabledIndexes = this.enabledTileIndexes();
    if (enabledIndexes.length === 0) {
      return;
    }

    if (!enabledIndexes.includes(this.visibleTileIndex())) {
      this.visibleTileIndex.set(enabledIndexes[0]);
    }

    const contentMap = this.tileContent();
    enabledIndexes.forEach(tileIndex => {
      const contents = contentMap.get(tileIndex) ?? [];
      if (contents.length <= 1) {
        return;
      }

      const rotationSeconds = this.tileConfig()[tileIndex]?.rotation_seconds ?? 7;
      const rotationMs = Math.max(rotationSeconds, 1) * 1000;
      const intervalId = window.setInterval(() => {
        this.visibleContentIndexes.update(prev => {
          const next = new Map(prev);
          const currentIndex = next.get(tileIndex) ?? 0;
          next.set(tileIndex, (currentIndex + 1) % contents.length);
          return next;
        });
      }, rotationMs);

      this.rotationIntervalIds.set(tileIndex, intervalId);
    });
  }

  private stopNewsRotation(): void {
    if (!this.isBrowser) {
      this.rotationIntervalIds.clear();
      return;
    }

    this.rotationIntervalIds.forEach(id => window.clearInterval(id));
    this.rotationIntervalIds.clear();
  }

  public onTileClick(tile: NewsTileContentDTO | undefined): void {
    if(!tile) {
      console.error('Cannot start navigation to news content because of an incorrect data input.');
    } else {
      const route = this.modConfigService.getRouteForGenericModelResource(tile.type, tile.id, tile.parent_id);
      if(route) {
        this.router.navigateTo(route);
      }
    }
  }
  
  /**
   * Handle image loading errors by marking the tile and using default image
   */
  public onImageError(tileIndex: number): void {
    this.imageLoadErrors.update(errors => {
      const newErrors = new Set(errors);
      newErrors.add(tileIndex);
      return newErrors;
    });
  }
  
  /**
   * Check if an image has failed to load
   */
  public hasImageError(tileIndex: number): boolean {
    return this.imageLoadErrors().has(tileIndex);
  }
  
  /**
   * Get the icon for a tile when image fails
   */
  public getTileIcon(tileIndex: number): string {
    const tileType = this.tileConfig()[tileIndex]?.type;
    const correspondingMenu = this.navService.userMenu().find(menu => menu.type === tileType);
    return correspondingMenu?.icon ?? 'image';
  }
  
  /**
   * Get the color for a tile icon
   */
  public getTileColor(tileIndex: number): string {
    const tileType = this.tileConfig()[tileIndex]?.type;
    const correspondingMenu = this.navService.userMenu().find(menu => menu.type === tileType);
    return correspondingMenu?.color ?? 'default';
  }

  private loadTileResources(conf: CustomerDashboardTileResource, tileIndex: number, type: SettingsMenuItemType): Observable<DashboardTileResource[]> {
    // Calculate skip value based on previous tiles with same type
    let skip = 0;
    for (let i = 0; i < tileIndex; i++) {
      const prevTile = this.tileConfig()[i];
      if (prevTile.enabled && this.hasAccessToTileType(prevTile.type) && prevTile.type === type) {
        skip += prevTile.entry_count;
      }
    }

    let retObservable: Observable<DashboardTileResource[]>;

    switch (type) {
      case SettingsMenuItemType.NEWS:
        retObservable = this.api.getDashboardTilesNews(conf.entry_count, skip);
        break;
      case SettingsMenuItemType.KNOWLEDGEBASE:
        retObservable = this.api.getDashboardTilesKnowledgebase(conf.entry_count, skip);
        break;
      case SettingsMenuItemType.COMMUNITIES:
        retObservable = this.api.getDashboardTilesCommunity(conf.entry_count, skip);
        break;
      default:
        retObservable = of([]);
    }

    return retObservable;
  }

  private hasAccessToTileType(type: string | null): boolean {
    if (!type) {
      return false;
    }
    const capabilities = this.capabilitiesService.capabilities();
    switch (type) {
      case SettingsMenuItemType.NEWS:
        return capabilities.news !== null;
      case SettingsMenuItemType.KNOWLEDGEBASE:
        return capabilities.knowledgebase !== null;
      case SettingsMenuItemType.COMMUNITIES:
        return capabilities.communities !== null;
      default:
        return false;
    }
  }
}
